import { apiClient, getAccessToken, refreshAccessToken } from './api';
import type { ScanAggregates } from '@utils/scanRunCharts';

/**
 * REV-131: client for the ad-hoc test execution endpoints.
 *
 * The backend is queue-based: POST /v1/tests returns 202 with a
 * scanRunId; progress is pushed via SSE on
 * GET /v1/tests/:id/stream; final results live on
 * GET /v1/tests/:id/results.
 */

export type ScanStatus = 'pending' | 'running' | 'completed' | 'failed' | 'cancelled';

export type WcagLevel =
  | 'A'
  | 'AA'
  | 'AAA'
  | 'wcag21a'
  | 'wcag21aa'
  | 'wcag21aaa'
  | 'wcag22a'
  | 'wcag22aa'
  | 'wcag22aaa';

/**
 * REV-477: 'adhoc' scans a single URL; 'crawl' runs a bounded, same-origin
 * whole-site crawl seeded from `url`. A crawl requires a contextId server-side.
 */
export type ScanType = 'adhoc' | 'crawl';

export interface RunTestInput {
  url: string;
  organizationId: string;
  contextId?: string;
  scanType?: ScanType;
  settings?: {
    wcagLevel?: WcagLevel;
    viewport?: { width: number; height: number };
    timeout?: number;
    captureScreenshot?: boolean;
    contextSelector?: string;
    auth?: Record<string, unknown>;
    headers?: Record<string, string>;
    // Crawl bounds (REV-477). Safety caps only — execution is never metered.
    maxPages?: number;
    maxDepth?: number;
    // Crawl scope (REV-485): which pages qualify, as opposed to how many.
    // `*`-globs matched against the whole absolute URL; exclude wins over
    // include. Omitted entirely when the user set no filter.
    includePatterns?: string[];
    excludePatterns?: string[];
    includeSubdomains?: boolean;
  };
}

export interface RunTestResponse {
  scanRunId: string;
  status: ScanStatus;
  url: string;
  message: string;
  // Present on ad-hoc scans; null/absent for crawls.
  ephemeralExpiresAt?: string | null;
  over_limit?: boolean;
  // Present on crawl scans (REV-477).
  scanType?: ScanType;
  maxPages?: number;
  maxDepth?: number;
  // REV-485: the scope the crawl actually accepted, after the server trimmed
  // and dropped unusable patterns.
  includePatterns?: string[];
  excludePatterns?: string[];
  includeSubdomains?: boolean;
}

/**
 * Scan run summary returned by GET /v1/tests/:id and the list endpoint.
 * REV-351: derived from the vendored zod contract schemas in
 * @contract/scanRun (which mirror revenant-api/src/contract/). Single
 * source of truth — these types and the backend-side validation share
 * the same definition. Replaces the previously hand-written interfaces
 * that drove the REV-345 type-drift bug.
 */
import type { z } from 'zod';
import type {
  ScanRunByIdSchema,
  ScanRunListItemSchema,
  TestResultItemSchema,
} from '@contract/scanRun';

export type ScanRunSummary = z.infer<typeof ScanRunByIdSchema>;
export type ScanRunListItem = z.infer<typeof ScanRunListItemSchema>;
export type TestResultItem = z.infer<typeof TestResultItemSchema>;

/**
 * GET /v1/tests/:id returns both the scan run summary and the per-target
 * results array. REV-132 surfaces the per-severity counts + density score
 * on TestRunDetailPage, so callers need both halves rather than the
 * scanRun-only shape this service returned previously.
 */
export interface ScanRunDetail {
  scanRun: ScanRunSummary;
  results: TestResultItem[] | null;
  /**
   * REV-711: run-level aggregates for the scan-results charts.
   *
   * Inferred from the vendored contract rather than hand-written. The two
   * fields above predate the contract and are hand-typed, which is the shape
   * of drift REV-345 was: a declared type and a real response that stopped
   * agreeing, silently. Optional because a deploy older than REV-711 will not
   * send it — the page renders the panels only when it is present.
   */
  aggregates?: ScanAggregates;
}

export interface ListScanRunsParams {
  organizationId?: string;
  contextId?: string;
  status?: ScanStatus;
  scanType?: string;
  page?: number;
  limit?: number;
}

export interface ListScanRunsResponse {
  items: ScanRunListItem[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

/**
 * Shape returned by GET /v1/tests/:id/results. NOT a superset of
 * ScanRunSummary — the results endpoint returns a much narrower payload
 * (just scanRunId, status, and the per-target results array).
 */
export interface ScanRunResults {
  scanRunId: string;
  status: ScanStatus;
  results: TestResultItem[];
}

export const testService = {
  async runTest(input: RunTestInput): Promise<RunTestResponse> {
    const res = await apiClient.post<{
      success: boolean;
      data: RunTestResponse;
      over_limit?: boolean;
    }>('/v1/tests', input);
    return { ...res.data, over_limit: res.over_limit };
  },

  async getTest(id: string, organizationId?: string): Promise<ScanRunDetail> {
    // The by-id endpoint wraps as { data: { scanRun, results } }. Returning
    // both halves so callers can render summary + per-target details from
    // a single fetch (REV-132).
    const res = await apiClient.get<{
      success: boolean;
      data: ScanRunDetail;
    }>(`/v1/tests/${id}`, organizationId ? { organizationId } : undefined);
    return res.data;
  },

  async getResults(id: string, organizationId?: string): Promise<ScanRunResults> {
    const res = await apiClient.get<{ success: boolean; data: ScanRunResults }>(
      `/v1/tests/${id}/results`,
      {
        includeIssues: 'true',
        ...(organizationId ? { organizationId } : {}),
      }
    );
    return res.data;
  },

  async cancel(id: string, organizationId?: string): Promise<void> {
    await apiClient.post(`/v1/tests/${id}/cancel`, organizationId ? { organizationId } : {});
  },

  async listScanRuns(params: ListScanRunsParams = {}): Promise<ListScanRunsResponse> {
    const res = await apiClient.get<{
      success: boolean;
      data: {
        scanRuns: ScanRunListItem[];
        pagination: ListScanRunsResponse['pagination'];
      };
    }>('/v1/tests', params as Record<string, unknown>);
    return {
      items: res.data.scanRuns,
      pagination: res.data.pagination,
    };
  },
};

export type ScanProgressEvent = {
  status?: ScanStatus;
  progress?: number;
  completedTargets?: number;
  totalTargets?: number;
  message?: string;
  errorMessage?: string;
  [key: string]: unknown;
};

/**
 * Parse a single SSE frame ("event: <name>\ndata: <json>") into its event name
 * and JSON payload. Returns null for comment/keepalive frames, empty frames, or
 * non-JSON data. The server emits NAMED events (status/progress/complete/error),
 * so we can't rely on EventSource's default "message" handler — we parse frames
 * ourselves. Exported for unit testing.
 */
export function parseSseFrame(frame: string): { event: string; data: ScanProgressEvent } | null {
  let event = 'message';
  const dataLines: string[] = [];
  for (const raw of frame.split('\n')) {
    const line = raw.replace(/\r$/, '');
    if (!line || line.startsWith(':')) continue; // blank or comment
    const colon = line.indexOf(':');
    const field = colon === -1 ? line : line.slice(0, colon);
    // Per the SSE spec a single leading space after the colon is stripped.
    const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
    if (field === 'event') event = value;
    else if (field === 'data') dataLines.push(value);
  }
  if (dataLines.length === 0) return null;
  try {
    return { event, data: JSON.parse(dataLines.join('\n')) as ScanProgressEvent };
  } catch {
    return null;
  }
}

/**
 * REV-131: subscribe to SSE progress events for a scan run. Returns a close()
 * function; consumers should call it on unmount.
 *
 * Uses fetch() streaming rather than the native EventSource because our SSE
 * endpoint authenticates via the `Authorization: Bearer` header, and EventSource
 * cannot set request headers (it can only send cookies). With EventSource the
 * stream 401'd for every user, so live progress never arrived. fetch() lets us
 * attach the bearer token AND read the raw stream so we can dispatch the server's
 * named events (status/progress/complete/error).
 */
/**
 * REV-572: reconnect budget for a dropped progress stream. The server closes an
 * already-finished scan's stream with `complete` immediately on connect, so one
 * successful reconnect is normally all it takes to recover. Backoff caps so a
 * genuinely dead endpoint is not hammered.
 */
const RECONNECT_BASE_MS = 500;
const RECONNECT_MAX_MS = 5000;
const MAX_RECONNECTS = 20;

export function subscribeToScanProgress(
  id: string,
  onEvent: (evt: ScanProgressEvent) => void,
  onError?: (err: unknown) => void
): () => void {
  if (typeof fetch === 'undefined') {
    return () => {};
  }
  const controller = new AbortController();
  const base = import.meta.env.VITE_API_URL?.replace(/\/$/, '') || '/api';

  const dispatch = (parsed: { event: string; data: ScanProgressEvent }) => {
    if (parsed.event === 'error') {
      onEvent({
        status: 'failed',
        errorMessage:
          typeof parsed.data.message === 'string' ? parsed.data.message : parsed.data.errorMessage,
      });
    } else {
      // status / progress / complete all carry a ScanProgressEvent-shaped payload.
      onEvent(parsed.data);
    }
  };

  // REV-572: whether the scan reached a terminal state on this subscription.
  // Until it does, the stream ending means the CONNECTION dropped, not that the
  // scan finished — and the two are indistinguishable from the reader's side.
  let sawTerminal = false;
  const TERMINAL = new Set(['completed', 'failed', 'cancelled']);

  const dispatchAndTrack = (parsed: { event: string; data: ScanProgressEvent }) => {
    if (parsed.event === 'complete' || TERMINAL.has(String(parsed.data?.status))) {
      sawTerminal = true;
    }
    dispatch(parsed);
  };

  /** One connection. Resolves when the stream ends, for whatever reason. */
  const readOnce = async (): Promise<void> => {
    // REV-802: the token is in memory and short-lived, and this raw fetch sits
    // outside the axios interceptor that renews it. Read it per connection, and
    // renew it here when it is missing (after a reload) or refused.
    const token = getAccessToken() ?? (await refreshAccessToken());
    const res = await fetch(`${base}/v1/tests/${id}/stream`, {
      method: 'GET',
      headers: {
        Accept: 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: 'include',
      signal: controller.signal,
    });
    if (res.status === 401) {
      // Renewed now so the reconnect below carries a fresh token.
      await refreshAccessToken();
    }
    if (!res.ok || !res.body) {
      throw new Error(`SSE stream failed: ${res.status}`);
    }
    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let sep: number;
      // Frames are separated by a blank line (\n\n).
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep);
        buffer = buffer.slice(sep + 2);
        const parsed = parseSseFrame(frame);
        if (parsed) dispatchAndTrack(parsed);
      }
    }
  };

  void (async () => {
    let delay = RECONNECT_BASE_MS;
    for (let attempt = 0; attempt <= MAX_RECONNECTS; attempt += 1) {
      try {
        await readOnce();
      } catch (err) {
        if (controller.signal.aborted) return;
        // A failed CONNECT (401, network refused) is reported once and retried
        // on the same schedule — the endpoint may simply not be up yet.
        onError?.(err);
      }
      // The three ways out: the caller unsubscribed, the scan reached a
      // terminal state, or we have retried enough that something is properly
      // wrong and silence is better than hammering the server.
      if (controller.signal.aborted || sawTerminal) return;
      if (attempt === MAX_RECONNECTS) {
        onError?.(new Error('SSE stream ended before the scan reported a terminal status'));
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, delay));
      if (controller.signal.aborted) return;
      delay = Math.min(delay * 2, RECONNECT_MAX_MS);
    }
  })();

  return () => controller.abort();
}
